import { useMemo, type ReactNode } from "react";
import { Box } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import NoTableEntries from "@components/DataTable/NoTableEntries";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useNotification } from "@context/NotificationContext";
import type { VesselCallStatusContextType } from "@features/vessel-call/vessel-call-status/VesselCallStatus";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { getPldaData } from "@services/pldaApi";
import { useQuery } from "@tanstack/react-query";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useOutletContext } from "react-router-dom";
import { mapPldaGridItem, type PldaGridRow } from "./pldaGrid.mapper";

const LANG_BASE = "vessel-call.operations.plda-grid";
const SERVICE = "bff-vessel";

const NOMINATION_PAQ_ROUTE = `/${paths.vesselNomination.index}/${paths.vesselNomination.details}/${paths.vesselNomination.paq}`;

type PldaGridDisplayRow = Omit<PldaGridRow, "nominationRefOrderNumber"> & {
  nominationRefOrderNumber: ReactNode;
};

const PldaGrid = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const outletContext = useOutletContext<VesselCallStatusContextType | undefined>();
  const vesselImo =
    outletContext?.vesselRegisterDetailsFixed?.vesselImoNumber ??
    outletContext?.vesselCallDetails?.generalDeclaration?.imoNumber;
  const userRoles = useAuthStore((state) => state.user?.roles ?? []);
  const isShippingAgent = userRoles.includes("shipping-agent");
  const handleClaim = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    notify({
      title: t(`${LANG_BASE}.actions.claim-success`),
      type: "success",
    });
  };

  const { data, isLoading } = useQuery({
    queryKey: ["plda-grid", SERVICE, vesselImo],
    queryFn: () => getPldaData(vesselImo!),
    enabled: Boolean(vesselImo),
  });

  const headers: Header[] = useMemo(
    () => [
      {
        field: "nominationRefOrderNumber",
        columnName: t(`${LANG_BASE}.columns.nomination-id`),
      },
      {
        field: "viaNumber",
        columnName: t(`${LANG_BASE}.columns.via-number`),
      },
      {
        field: "pldaNumber",
        columnName: t(`${LANG_BASE}.columns.plda-number`),
      },
      {
        field: "pldaSubmitted",
        columnName: t(`${LANG_BASE}.columns.plda-submitted`),
      },
      {
        field: "vesselNor",
        columnName: t(`${LANG_BASE}.columns.vessel-nor`),
      },
      {
        field: "allCustomerAcceptance",
        columnName: t(`${LANG_BASE}.columns.all-customer-acceptance`),
      },
      {
        field: "fottApproved",
        columnName: t(`${LANG_BASE}.columns.fott-approved`),
      },
      {
        field: "masterSignature",
        columnName: t(`${LANG_BASE}.columns.master-signature`),
      },
      {
        field: "status",
        columnName: t(`${LANG_BASE}.columns.status`),
      },
    ],
    [t],
  );

  const rows = useMemo<PldaGridDisplayRow[]>(
    () =>
      (data ?? []).map((item) => {
        const row = mapPldaGridItem(item, t);

        return {
          ...row,
          nominationRefOrderNumber: (
            <Link
              className="text-blue-600 "
              to={generatePath(NOMINATION_PAQ_ROUTE, { id: row.nominationId })}
            >
              {row.nominationRefOrderNumber}
            </Link>
          ),
        };
      }),
    [data, t],
  );

  if (isLoading) {
    return (
      <div
        data-testid="operations-liquid-bulk-plda-loading"
        className="relative flex min-h-100 w-full items-center justify-center py-4"
      >
        <LoadingOverlay loading />
      </div>
    );
  }

  return (
    <div data-testid="operations-liquid-bulk-plda" className="w-full py-4">
      {rows.length === 0 ? (
        <div className="flex min-h-100 items-center justify-center">
          <NoTableEntries
            noMargin
            dataName=""
            actionName=""
            customMessage={`${LANG_BASE}.not-submitted`}
          />
        </div>
      ) : (
        <Box
          sx={{
            "& > .MuiBox-root > .MuiBox-root:last-child": {
              display: "none",
            },
          }}
        >
          <CustomDataTable<PldaGridDisplayRow>
            title=""
            actionName=""
            rows={rows}
            headers={headers}
            testId="operations-liquid-bulk-plda-grid"
            page={1}
            pageSize={25}
            totalCount={rows.length}
            navigationLinkBase=""
            filters={[]}
            onApplyFilters={() => {}}
            availableFields={{}}
            hideFilters
            displaySubTitle={false}
            shouldFreezeExtraColumn={isShippingAgent}
            actionColumn={
              isShippingAgent
                ? {
                    width: 160,
                    actionTitle: t(`${LANG_BASE}.actions.claim`),
                    onActionClick: handleClaim,
                  }
                : undefined
            }
          />
        </Box>
      )}
    </div>
  );
};

export default PldaGrid;
